import React, { useMemo, useState, useEffect } from 'react';
import PageLayout from '../components/PageLayout';
import usePortfolioSocket from '../hooks/usePortfolioSocket';

const Pill = ({ children, tone = 'default' }) => {
  const toneCls = tone === 'success' ? 'text-success' : tone === 'danger' ? 'text-destructive' : 'text-textPrimary';
  return <span className={`px-2 py-0.5 rounded border border-borderDefault ${toneCls}`}>{children}</span>;
};

export default function PortfolioPage() {
  const [selectedIds, setSelectedIds] = useState(null); // null/undefined = all
  const { ready, accountsById, positionsByAccount, pendingByAccount, lastEventAt, reconnect, error } = usePortfolioSocket({ accountIds: selectedIds || undefined });
  const [debugLines, setDebugLines] = useState([]);

  // Tap into console logs for this page session to show recent messages (optional light hook)
  useEffect(() => {
    const origLog = console.log;
    const origWarn = console.warn;
    const origErr = console.error;
    function push(kind, args) {
      try {
        const txt = args.map((a) => (typeof a === 'string' ? a : JSON.stringify(a))).join(' ');
        setDebugLines((prev) => [
          `${new Date().toLocaleTimeString()} [${kind}] ${txt}`,
          ...prev.slice(0, 199),
        ]);
      } catch {}
    }
    console.log = (...args) => { push('log', args); origLog(...args); };
    console.warn = (...args) => { push('warn', args); origWarn(...args); };
    console.error = (...args) => { push('error', args); origErr(...args); };
    return () => {
      console.log = origLog;
      console.warn = origWarn;
      console.error = origErr;
    };
  }, []);

  const accountIds = useMemo(() => Object.keys(accountsById || {}), [accountsById]);

  const flattenedPositions = useMemo(() => {
    const rows = [];
    for (const id of Object.keys(positionsByAccount || {})) {
      (positionsByAccount[id] || []).forEach((p) => rows.push({ ...p, accountId: id }));
    }
    return rows;
  }, [positionsByAccount]);

  const flattenedPending = useMemo(() => {
    const rows = [];
    for (const id of Object.keys(pendingByAccount || {})) {
      (pendingByAccount[id] || []).forEach((o) => rows.push({ ...o, accountId: id }));
    }
    return rows;
  }, [pendingByAccount]);

  const toggleId = (id) => {
    setSelectedIds((prev) => {
      if (!prev) return [id];
      return prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
    });
  };

  const clearFilter = () => setSelectedIds(null);

  const lastStr = lastEventAt ? new Date(lastEventAt).toLocaleTimeString() : '—';

  return (
    <PageLayout>
      <div className="p-4 space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold text-primary">All Accounts Portfolio</h1>
          <div className="flex items-center gap-3 text-sm">
            <Pill tone={ready ? 'success' : 'danger'}>{ready ? 'Live' : 'Connecting...'}</Pill>
            <span className="text-textSecondary">Last event: {lastStr}</span>
            <button className="btn btn-secondary" onClick={reconnect}>Reconnect</button>
          </div>
        </div>

        {error && (
          <div className="p-3 border border-destructive rounded text-destructive bg-destructive/5">
            {error.message || String(error)}
          </div>
        )}

        {/* Filter */}
        <div className="bg-backgroundSurface p-4 rounded shadow">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-medium text-textPrimary">Account Filter</h2>
            <div className="flex items-center gap-2">
              <button className="btn btn-neutral" onClick={clearFilter} disabled={!selectedIds}>Clear</button>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {accountIds.length === 0 && <div className="text-textSecondary">No accounts</div>}
            {accountIds.map((id) => (
              <label key={id} className="flex items-center gap-2 p-2 rounded border border-borderDefault hover:bg-secondary/10 cursor-pointer">
                <input
                  type="checkbox"
                  className="checkbox"
                  checked={selectedIds ? selectedIds.includes(id) : true}
                  onChange={() => toggleId(id)}
                />
                <span className="truncate">{id}</span>
                <span className="ml-auto text-xs text-textSecondary">{accountsById[id]?.platform}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Accounts summary */}
        <div className="bg-backgroundSurface p-4 rounded shadow">
          <h2 className="text-lg font-medium mb-3">Accounts</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {accountIds.map((id) => {
              const acc = accountsById[id] || {};
              return (
                <div key={id} className="p-3 rounded border border-borderDefault">
                  <div className="flex items-center justify-between">
                    <div className="font-semibold">{id}</div>
                    <div className="text-xs text-textSecondary">{acc.platform}</div>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                    <div className="text-textSecondary">Currency</div>
                    <div className="text-textPrimary">{acc.currency || '—'}</div>
                    <div className="text-textSecondary">Balance</div>
                    <div className="text-success">{typeof acc.balance === 'number' ? acc.balance.toFixed(2) : '—'}</div>
                    <div className="text-textSecondary">Equity</div>
                    <div className="text-success">{typeof acc.equity === 'number' ? acc.equity.toFixed(2) : '—'}</div>
                    <div className="text-textSecondary">Margin</div>
                    <div className="text-textPrimary">{typeof acc.margin === 'number' ? acc.margin.toFixed(2) : '—'}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Positions */}
        <div className="bg-backgroundSurface p-4 rounded shadow">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-medium">Open Positions</h2>
            <div className="text-sm text-textSecondary">{flattenedPositions.length} rows</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1000px] border-collapse text-sm">
              <thead>
                <tr className="bg-secondary">
                  <th className="border border-borderDefault p-2">Account</th>
                  <th className="border border-borderDefault p-2">Symbol</th>
                  <th className="border border-borderDefault p-2">Side</th>
                  <th className="border border-borderDefault p-2">Lots</th>
                  <th className="border border-borderDefault p-2">Open</th>
                  <th className="border border-borderDefault p-2">SL</th>
                  <th className="border border-borderDefault p-2">TP</th>
                  <th className="border border-borderDefault p-2">P/L</th>
                  <th className="border border-borderDefault p-2">Time</th>
                </tr>
              </thead>
              <tbody>
                {flattenedPositions.length === 0 ? (
                  <tr>
                    <td className="text-center text-textSecondary p-4" colSpan={9}>No positions</td>
                  </tr>
                ) : (
                  flattenedPositions.map((p, i) => (
                    <tr key={`${p.accountId}-${p.ticket || i}`} className="hover:bg-secondary/10">
                      <td className="border border-borderDefault p-2">{p.accountId}</td>
                      <td className="border border-borderDefault p-2">{p.symbol}</td>
                      <td className={`border border-borderDefault p-2 ${p.direction === 'BUY' ? 'text-success' : 'text-destructive'}`}>{p.direction}</td>
                      <td className="border border-borderDefault p-2">{p.volume ?? '—'}</td>
                      <td className="border border-borderDefault p-2">{p.price_open ?? '—'}</td>
                      <td className="border border-borderDefault p-2">{p.sl ?? '—'}</td>
                      <td className="border border-borderDefault p-2">{p.tp ?? '—'}</td>
                      <td className={`border border-borderDefault p-2 ${parseFloat(p.profit) >= 0 ? 'text-success' : 'text-destructive'}`}>{p.profit ?? '—'}</td>
                      <td className="border border-borderDefault p-2">{p.time ? new Date(p.time * 1000).toLocaleString() : '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pending Orders */}
        <div className="bg-backgroundSurface p-4 rounded shadow">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-medium">Pending Orders</h2>
            <div className="text-sm text-textSecondary">{flattenedPending.length} rows</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] border-collapse text-sm">
              <thead>
                <tr className="bg-secondary">
                  <th className="border border-borderDefault p-2">Account</th>
                  <th className="border border-borderDefault p-2">Symbol</th>
                  <th className="border border-borderDefault p-2">Type</th>
                  <th className="border border-borderDefault p-2">Lots</th>
                  <th className="border border-borderDefault p-2">Price</th>
                  <th className="border border-borderDefault p-2">SL</th>
                  <th className="border border-borderDefault p-2">TP</th>
                  <th className="border border-borderDefault p-2">Time</th>
                </tr>
              </thead>
              <tbody>
                {flattenedPending.length === 0 ? (
                  <tr>
                    <td className="text-center text-textSecondary p-4" colSpan={8}>No pending orders</td>
                  </tr>
                ) : (
                  flattenedPending.map((o, i) => (
                    <tr key={`${o.accountId}-${o.order_id || i}`} className="hover:bg-secondary/10">
                      <td className="border border-borderDefault p-2">{o.accountId}</td>
                      <td className="border border-borderDefault p-2">{o.symbol}</td>
                      <td className="border border-borderDefault p-2">{o.type}</td>
                      <td className="border border-borderDefault p-2">{o.volume ?? '—'}</td>
                      <td className="border border-borderDefault p-2">{o.price ?? '—'}</td>
                      <td className="border border-borderDefault p-2">{o.sl ?? '—'}</td>
                      <td className="border border-borderDefault p-2">{o.tp ?? '—'}</td>
                      <td className="border border-borderDefault p-2">{o.time ? new Date(o.time * 1000).toLocaleString() : '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Debug console */}
        <div className="bg-backgroundSurface p-3 rounded shadow">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-medium">Debug Logs</h2>
            <button className="btn btn-neutral btn-sm" onClick={() => setDebugLines([])}>Clear</button>
          </div>
          <div className="max-h-64 overflow-auto text-xs font-mono whitespace-pre-wrap bg-backgroundBody p-2 rounded border border-borderDefault">
            {debugLines.length === 0 ? 'No logs yet…' : debugLines.join('\n')}
          </div>
        </div>
      </div>
    </PageLayout>
  );
}
